import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './api';

export const DEFAULT_BADGE_ICON = 'badge-default';

export type ProgressionLocaleContent = {
  name: string;
  description: string;
};

export type ProgressionLocales = {
  en: ProgressionLocaleContent;
  fr: ProgressionLocaleContent;
};

export type Badge = {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  category?: string;
  rarity?: string;
  tags?: string[];
  locales?: ProgressionLocales;
};

export type DocumentGroupCondition = {
  type: 'domain' | 'specific' | 'level_path' | 'custom';
  domain?: string;
  domainCount?: number;
  domainOrder?: 'first' | 'any' | 'sequential';
  specificDocuments?: Array<{ documentCode: string; order?: number }>;
  level?: number;
  pathType?: 'pathA' | 'pathB' | 'both';
  customCriteria?: {
    domain?: string;
    docType?: string;
    tags?: string[];
    count?: number;
  };
  minProgression?: number;
  allRequired?: boolean;
};

export type GroupBadge = {
  _id: string;
  name: string;
  description?: string;
  bonusExperiencePoints?: number;
  isActive?: boolean;
  documentGroup: DocumentGroupCondition;
  badgeId: Badge | string;
  tags?: string[];
};

export type PathDocument = {
  documentId?: string;
  documentCode?: string;
  documentTitle?: string;
  documentDomain?: string;
  minProgression?: number;
  order?: number;
};

export type LevelPath = {
  pathName?: string;
  description?: string;
  documents?: PathDocument[];
};

export type ChallengeCondition =
  | {
      type: 'complete_documents';
      documents?: Array<{ documentCode?: string; documentId?: string; minProgression?: number; order?: number }>;
      minProgression?: number;
    }
  | {
      type: 'complete_count_in_group';
      documents?: Array<{ documentCode?: string; documentId?: string; minProgression?: number; order?: number }>;
      count?: number;
      minProgression?: number;
    }
  | {
      type: 'complete_count_in_period';
      itemKind?: 'documents' | 'exercises';
      count?: number;
      period?: 'rolling_days' | 'calendar_week' | 'calendar_month' | 'challenge_window';
      periodDays?: number;
      minProgression?: number;
      filters?: { docDomain?: string; docType?: string };
    }
  | {
      type: 'social_friends';
      minFriends?: number;
    }
  | {
      type: 'admin_verification';
      verificationKind?: string;
    };

export type UserChallengeProgress = {
  status?: 'in_progress' | 'completed';
  completedAt?: string;
  rewardsGranted?: boolean;
  conditionProgress?: Array<{
    type?: string;
    met?: boolean;
    detail?: string;
  }>;
};

export type UserChallengeItem = {
  challenge: Challenge;
  progress: UserChallengeProgress | null;
};

export type ChallengeRewards = {
  experiencePoints?: number;
  badgeIds?: Array<Badge | string>;
};

export type Challenge = {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
  startsAt?: string;
  endsAt?: string;
  conditionMode?: 'all' | 'any';
  conditions: ChallengeCondition[];
  rewards?: ChallengeRewards;
  tags?: string[];
  locales?: ProgressionLocales;
};

export type AdvancementRequirements = {
  minTotalExperience?: number;
  requiredBadgeIds?: string[];
  requiredGroupBadgeIds?: string[];
  requiredChallengeIds?: string[];
  pathCompletion?: {
    mode?: 'pathA' | 'pathB' | 'either' | 'both';
    minProgression?: number;
  };
  levelCompletionBonusXP?: number;
  levelRewardBadgeIds?: string[];
};

export type LevelRequirement = {
  _id: string;
  level: number;
  levelName?: string;
  description?: string;
  pathA?: LevelPath;
  pathB?: LevelPath;
  advancementRequirements?: AdvancementRequirements;
};

export type InitialConfig = {
  _id: string;
  level: number;
  name?: string;
  description?: string;
  isActive?: boolean;
  documents?: Array<{
    documentCode?: string;
    domain?: string;
    initialProgression?: number;
    order?: number;
  }>;
};

export async function getInitialProgressionConfigs() {
  return apiGet<{ success: boolean; data?: { configs: InitialConfig[] } }>(
    '/api/v1/progression/initial-config'
  );
}

export async function getBadges() {
  return apiGet<{ success: boolean; data?: { badges: Badge[] } }>('/api/v1/progression/badges');
}

export async function getProgressionLevels() {
  return apiGet<{ success: boolean; data?: { levels: LevelRequirement[] } }>(
    '/api/v1/progression/levels'
  );
}

export async function createBadge(body: Partial<Badge>) {
  return apiPost<{ success: boolean; data?: { badge: Badge } }>('/api/v1/progression/badges', body);
}

export async function updateBadge(badgeId: string, body: Partial<Badge>) {
  return apiPut<{ success: boolean; data?: { badge: Badge } }>(
    `/api/v1/progression/badges/${badgeId}`,
    body
  );
}

export async function deleteBadge(badgeId: string) {
  return apiDelete<{ success: boolean; message?: string }>(`/api/v1/progression/badges/${badgeId}`);
}

export async function createLevel(body: Record<string, unknown>) {
  return apiPost<{ success: boolean; data?: { level: LevelRequirement } }>(
    '/api/v1/progression/levels',
    body
  );
}

export async function updateLevel(levelId: string, body: Record<string, unknown>) {
  return apiPut<{ success: boolean; data?: { level: LevelRequirement } }>(
    `/api/v1/progression/levels/${levelId}`,
    body
  );
}

export async function deleteLevel(levelId: string) {
  return apiDelete<{ success: boolean; message?: string }>(`/api/v1/progression/levels/${levelId}`);
}

export async function createInitialConfig(body: Record<string, unknown>) {
  return apiPost<{ success: boolean; data?: { config: InitialConfig } }>(
    '/api/v1/progression/initial-config',
    body
  );
}

export async function updateInitialConfig(configId: string, body: Record<string, unknown>) {
  return apiPut<{ success: boolean; data?: { config: InitialConfig } }>(
    `/api/v1/progression/initial-config/${configId}`,
    body
  );
}

export async function deleteInitialConfig(configId: string) {
  return apiDelete<{ success: boolean; message?: string }>(
    `/api/v1/progression/initial-config/${configId}`
  );
}

export async function activateInitialConfig(configId: string) {
  return apiPatch<{ success: boolean; data?: { config: InitialConfig } }>(
    `/api/v1/progression/initial-config/${configId}/activate`,
    {}
  );
}

export type ExperienceHistoryEntry = {
  amount: number;
  source: string;
  timestamp?: string;
  reason?: string;
  documentCode?: string;
};

export async function getExperienceHistory(userId: string, params: { limit?: number; skip?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.skip) qs.set('skip', String(params.skip));
  const query = qs.toString();
  return apiGet<{
    success: boolean;
    data?: { history: ExperienceHistoryEntry[]; totalExperience: number; total: number };
  }>(`/api/v1/progression/user/${userId}/experience/history${query ? `?${query}` : ''}`);
}

export async function adjustUserExperience(userId: string, amount: number, reason?: string) {
  return apiPost<{ success: boolean; message?: string }>(
    `/api/v1/progression/user/${userId}/experience/adjust`,
    { amount, reason }
  );
}

export async function grantUserBadge(userId: string, badgeId: string, reason?: string) {
  return apiPost<{ success: boolean; message?: string; data?: { badge: Badge } }>(
    `/api/v1/progression/user/${userId}/badges/${badgeId}/grant`,
    { reason }
  );
}

export async function revokeUserBadge(userId: string, badgeId: string) {
  return apiDelete<{ success: boolean; message?: string }>(
    `/api/v1/progression/user/${userId}/badges/${badgeId}`
  );
}

export async function getUserChallenges(userId: string) {
  return apiGet<{ success: boolean; data?: { challenges: UserChallengeItem[] } }>(
    `/api/v1/progression/user/${userId}/challenges`
  );
}

export async function completeUserChallenge(userId: string, challengeId: string) {
  return apiPost<{
    success: boolean;
    message?: string;
    data?: { award?: { xpGranted?: number; badgesGranted?: number } };
  }>(`/api/v1/progression/user/${userId}/challenges/${challengeId}/complete`, {});
}

export async function advanceUserLevel(userId: string) {
  return apiPost<{ success: boolean; message?: string; data?: Record<string, unknown> }>(
    `/api/v1/progression/user/${userId}/advance`,
    {}
  );
}

export async function initializeUserProgression(userId: string, level = 1) {
  return apiPost<{ success: boolean; message?: string; data?: { documentsInitialized?: number } }>(
    `/api/v1/progression/user/${userId}/initialize`,
    { level }
  );
}

export async function getGroupBadges(params: { isActive?: boolean; search?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.isActive === true) qs.set('isActive', 'true');
  if (params.isActive === false) qs.set('isActive', 'false');
  if (params.search) qs.set('search', params.search);
  const query = qs.toString();
  return apiGet<{ success: boolean; data?: { groupBadges: GroupBadge[] } }>(
    `/api/v1/progression/group-badges${query ? `?${query}` : ''}`
  );
}

export async function createGroupBadge(body: Record<string, unknown>) {
  return apiPost<{ success: boolean; data?: { groupBadge: GroupBadge } }>(
    '/api/v1/progression/group-badges',
    body
  );
}

export async function updateGroupBadge(id: string, body: Record<string, unknown>) {
  return apiPut<{ success: boolean; data?: { groupBadge: GroupBadge } }>(
    `/api/v1/progression/group-badges/${id}`,
    body
  );
}

export async function deleteGroupBadge(id: string) {
  return apiDelete<{ success: boolean; message?: string }>(
    `/api/v1/progression/group-badges/${id}`
  );
}

export async function toggleGroupBadgeActive(id: string, isActive?: boolean) {
  return apiPatch<{ success: boolean; data?: { groupBadge: GroupBadge } }>(
    `/api/v1/progression/group-badges/${id}/activate`,
    isActive !== undefined ? { isActive } : {}
  );
}

export async function getChallenges(params: { isActive?: boolean; search?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.isActive === true) qs.set('isActive', 'true');
  if (params.isActive === false) qs.set('isActive', 'false');
  if (params.search) qs.set('search', params.search);
  const query = qs.toString();
  return apiGet<{ success: boolean; data?: { challenges: Challenge[] } }>(
    `/api/v1/progression/challenges${query ? `?${query}` : ''}`
  );
}

export async function createChallenge(body: Record<string, unknown>) {
  return apiPost<{ success: boolean; data?: { challenge: Challenge } }>(
    '/api/v1/progression/challenges',
    body
  );
}

export async function updateChallenge(id: string, body: Record<string, unknown>) {
  return apiPut<{ success: boolean; data?: { challenge: Challenge } }>(
    `/api/v1/progression/challenges/${id}`,
    body
  );
}

export async function deleteChallenge(id: string) {
  return apiDelete<{ success: boolean; message?: string }>(
    `/api/v1/progression/challenges/${id}`
  );
}

export async function toggleChallengeActive(id: string, isActive?: boolean) {
  return apiPatch<{ success: boolean; data?: { challenge: Challenge } }>(
    `/api/v1/progression/challenges/${id}/activate`,
    isActive !== undefined ? { isActive } : {}
  );
}
